import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Container,
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Box,
	IconButton,
	Chip,
	Menu,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Alert,
	CircularProgress,
} from "@mui/material";
import {
	Add,
	MoreVert,
	Edit,
	Delete,
	Upload,
	Download,
	Storage,
	Link as LinkIcon,
	ContentCopy,
} from "@mui/icons-material";
import {
	fetchDataSets,
	createDataSet,
	updateDataSet,
	deleteDataSet,
	importCSV,
	exportCSV,
	linkTestToDataSet,
	unlinkTestFromDataSet,
} from "../store/slices/dataSetSlice";
import testService from "../services/testService";
import CloneDialog from "../components/CloneDialog";
import OwnerBadge from "../components/OwnerBadge";
import { canModify } from "../utils/permissions";

function DataSets() {
	const dispatch = useDispatch();
	const { dataSets, loading, error } = useSelector((state) => state.datasets);
	const { user } = useSelector((state) => state.auth);
	const { selectedProject } = useSelector((state) => state.projects);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [viewDialogOpen, setViewDialogOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [selectedDataSet, setSelectedDataSet] = useState(null);
	const [contextMenu, setContextMenu] = useState(null);
	const [csvFile, setCsvFile] = useState(null);
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkDataSet, setLinkDataSet] = useState(null);
	const [availableTests, setAvailableTests] = useState([]);

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		columns: [],
		rows: [],
	});

	const [cloneTarget, setCloneTarget] = useState(null);

	useEffect(() => {
		dispatch(
			fetchDataSets({
				workspaceId: "default",
			}),
		);
	}, [dispatch]);

	const handleOpenDialog = (dataSet = null) => {
		if (dataSet) {
			setEditMode(true);
			setFormData({
				name: dataSet.name,
				description: dataSet.description || "",
				columns: [...(dataSet.columns || [])],
				rows: (dataSet.rows || []).map((row) => ({ ...row })),
			});
			setSelectedDataSet(dataSet);
		} else {
			setEditMode(false);
			setFormData({
				name: "",
				description: "",
				columns: ["column1", "column2"],
				rows: [{ column1: "", column2: "" }],
			});
			setSelectedDataSet(null);
		}
		setDialogOpen(true);
		setContextMenu(null);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditMode(false);
		setFormData({
			name: "",
			description: "",
			columns: [],
			rows: [],
		});
		setSelectedDataSet(null);
	};

	const handleSubmit = async () => {
		const dataSetData = {
			...formData,
			workspaceId: "default",
			projectId: selectedProject?.id,
			userId: user?.id,
			sourceType: "MANUAL",
		};

		if (editMode) {
			await dispatch(
				updateDataSet({
					id: selectedDataSet.id,
					dataSet: {
						...dataSetData,
						status: selectedDataSet.status || "ACTIVE",
					},
				}),
			);
		} else {
			await dispatch(createDataSet(dataSetData));
		}

		handleCloseDialog();
	};

	const handleDelete = async (dataSet) => {
		if (window.confirm(`Delete dataset "${dataSet.name}"?`)) {
			await dispatch(deleteDataSet(dataSet.id));
		}
		setContextMenu(null);
	};

	const handleContextMenu = (event, dataSet) => {
		event.preventDefault();
		setContextMenu({
			mouseX: event.clientX - 2,
			mouseY: event.clientY - 4,
			dataSet,
		});
	};

	const handleImportCSV = async () => {
		if (!csvFile) return;

		await dispatch(
			importCSV({
				file: csvFile,
				workspaceId: "default",
				projectId: selectedProject?.id,
				userId: user?.id,
			}),
		);

		setCsvFile(null);
		setImportDialogOpen(false);
	};

	const handleExportCSV = async (dataSetId, name) => {
		const blob = await dispatch(exportCSV(dataSetId)).unwrap();
		const url = window.URL.createObjectURL(new Blob([blob]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `${name}.csv`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		setContextMenu(null);
	};

	const handleViewDataSet = (dataSet) => {
		setSelectedDataSet(dataSet);
		setViewDialogOpen(true);
		setContextMenu(null);
	};

	const handleOpenLinkDialog = async (dataSet) => {
		setLinkDataSet(dataSet);
		setContextMenu(null);
		try {
			const params = { workspaceId: "default" };
			if (selectedProject?.id) params.projectId = selectedProject.id;
			const response = await testService.getTests(params);
			const tests = response.data || response;
			const linked = dataSet.linkedTestIds || [];
			setAvailableTests(
				Array.isArray(tests)
					? tests.filter((t) => !linked.includes(t.id))
					: [],
			);
		} catch {
			setAvailableTests([]);
		}
		setLinkDialogOpen(true);
	};

	const handleLinkTest = async (testId) => {
		if (!linkDataSet) return;
		await dispatch(
			linkTestToDataSet({ dataSetId: linkDataSet.id, testId }),
		);
		setAvailableTests((prev) => prev.filter((t) => t.id !== testId));
	};

	const handleUnlinkTest = async (dataSetId, testId) => {
		await dispatch(unlinkTestFromDataSet({ dataSetId, testId }));
	};

	const addColumn = () => {
		const newColumnName = `column${formData.columns.length + 1}`;
		setFormData({
			...formData,
			columns: [...formData.columns, newColumnName],
			rows: formData.rows.map((row) => ({ ...row, [newColumnName]: "" })),
		});
	};

	const removeColumn = (columnName) => {
		setFormData({
			...formData,
			columns: formData.columns.filter((col) => col !== columnName),
			rows: formData.rows.map((row) => {
				const newRow = { ...row };
				delete newRow[columnName];
				return newRow;
			}),
		});
	};

	const addRow = () => {
		const newRow = {};
		formData.columns.forEach((col) => (newRow[col] = ""));
		setFormData({
			...formData,
			rows: [...formData.rows, newRow],
		});
	};

	const removeRow = (index) => {
		setFormData({
			...formData,
			rows: formData.rows.filter((_, i) => i !== index),
		});
	};

	const updateCellValue = (rowIndex, columnName, value) => {
		const newRows = formData.rows.map((row, i) =>
			i === rowIndex ? { ...row, [columnName]: value } : row,
		);
		setFormData({ ...formData, rows: newRows });
	};

	const updateColumnName = (oldName, newName) => {
		const newColumns = formData.columns.map((col) =>
			col === oldName ? newName : col,
		);
		const newRows = formData.rows.map((row) => {
			const newRow = { ...row };
			newRow[newName] = newRow[oldName];
			delete newRow[oldName];
			return newRow;
		});
		setFormData({ ...formData, columns: newColumns, rows: newRows });
	};

	if (loading && dataSets.length === 0) {
		return (
			<Container
				maxWidth="xl"
				sx={{
					mt: 4,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "50vh",
				}}
			>
				<CircularProgress />
			</Container>
		);
	}

	return (
		<Container maxWidth="xl" sx={{ mt: 4 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={4}
			>
				<Typography variant="h4">Data Sets</Typography>
				<Box display="flex" gap={2}>
					<Button
						variant="outlined"
						startIcon={<Upload />}
						onClick={() => setImportDialogOpen(true)}
					>
						Import CSV
					</Button>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => handleOpenDialog()}
					>
						New Data Set
					</Button>
				</Box>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{dataSets.length === 0 ? (
				<Paper sx={{ p: 6, textAlign: "center" }}>
					<Storage
						sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
					/>
					<Typography variant="h6" gutterBottom>
						No Data Sets Yet
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: 3 }}
					>
						Create data sets to run data-driven tests with multiple
						input values
					</Typography>
					<Box display="flex" gap={2} justifyContent="center">
						<Button
							variant="contained"
							startIcon={<Add />}
							onClick={() => handleOpenDialog()}
						>
							Create Data Set
						</Button>
						<Button
							variant="outlined"
							startIcon={<Upload />}
							onClick={() => setImportDialogOpen(true)}
						>
							Import CSV
						</Button>
					</Box>
				</Paper>
			) : (
				<Grid container spacing={3}>
					{dataSets.map((dataSet) => (
						<Grid item xs={12} sm={6} md={4} key={dataSet.id}>
							<Card
								sx={{
									cursor: "pointer",
									"&:hover": { boxShadow: 6 },
								}}
								onContextMenu={(e) =>
									handleContextMenu(e, dataSet)
								}
								onClick={() => handleViewDataSet(dataSet)}
							>
								<CardContent>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="flex-start"
									>
										<Box
											display="flex"
											alignItems="center"
											gap={1}
										>
											<Storage color="primary" />
											<Box>
												<Typography variant="h6">
													{dataSet.name}
												</Typography>
												<OwnerBadge item={dataSet} />
											</Box>
										</Box>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleContextMenu(e, dataSet);
											}}
										>
											<MoreVert />
										</IconButton>
									</Box>

									{dataSet.description && (
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mt: 1 }}
										>
											{dataSet.description}
										</Typography>
									)}

									<Box
										display="flex"
										gap={1}
										mt={2}
										flexWrap="wrap"
									>
										<Chip
											label={`${dataSet.columns?.length || 0} columns`}
											size="small"
											variant="outlined"
										/>
										<Chip
											label={`${dataSet.rows?.length || 0} rows`}
											size="small"
											variant="outlined"
										/>
										<Chip
											label={`${dataSet.linkedTestIds?.length || dataSet.usageCount || 0} tests`}
											size="small"
											color="primary"
											icon={<LinkIcon />}
										/>
									</Box>

									<Box display="flex" gap={1} mt={1}>
										<Chip
											label={
												dataSet.sourceType || "MANUAL"
											}
											size="small"
											color="secondary"
										/>
										<Chip
											label={dataSet.status || "ACTIVE"}
											size="small"
											color={
												dataSet.status === "ACTIVE"
													? "success"
													: "default"
											}
										/>
									</Box>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			)}

			{/* Context Menu */}
			<Menu
				open={contextMenu !== null}
				onClose={() => setContextMenu(null)}
				anchorReference="anchorPosition"
				anchorPosition={
					contextMenu !== null
						? { top: contextMenu.mouseY, left: contextMenu.mouseX }
						: undefined
				}
			>
				<MenuItem
					onClick={() => {
						handleViewDataSet(contextMenu.dataSet);
					}}
				>
					<Storage sx={{ mr: 1 }} fontSize="small" />
					View Data
				</MenuItem>
				{/* Owner or admin: edit, link, delete. Others view, export and clone. */}
				{canModify(contextMenu?.dataSet) && (
					<MenuItem
						onClick={() => {
							handleOpenDialog(contextMenu.dataSet);
						}}
					>
						<Edit sx={{ mr: 1 }} fontSize="small" />
						Edit
					</MenuItem>
				)}
				<MenuItem
					onClick={() =>
						handleExportCSV(
							contextMenu.dataSet.id,
							contextMenu.dataSet.name,
						)
					}
				>
					<Download sx={{ mr: 1 }} fontSize="small" />
					Export CSV
				</MenuItem>
				<MenuItem
					onClick={() => {
						setCloneTarget(contextMenu.dataSet);
						setContextMenu(null);
					}}
				>
					<ContentCopy sx={{ mr: 1 }} fontSize="small" />
					Clone
				</MenuItem>
				{canModify(contextMenu?.dataSet) && (
					<MenuItem
						onClick={() => handleOpenLinkDialog(contextMenu.dataSet)}
					>
						<LinkIcon sx={{ mr: 1 }} fontSize="small" />
						Link Test
					</MenuItem>
				)}
				{canModify(contextMenu?.dataSet) && (
					<MenuItem
						onClick={() => {
							handleDelete(contextMenu.dataSet);
						}}
						sx={{ color: "error.main" }}
					>
						<Delete sx={{ mr: 1 }} fontSize="small" />
						Delete
					</MenuItem>
				)}
			</Menu>

			<CloneDialog
				open={!!cloneTarget}
				kind="dataSet"
				item={cloneTarget}
				onClose={() => setCloneTarget(null)}
				onCloned={() => dispatch(fetchDataSets({ workspaceId: "default" }))}
			/>

			{/* Create/Edit Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle>
					{editMode ? "Edit Data Set" : "Create Data Set"}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2 }}>
						<TextField
							label="Name"
							fullWidth
							value={formData.name}
							onChange={(e) =>
								setFormData({
									...formData,
									name: e.target.value,
								})
							}
							sx={{ mb: 2 }}
						/>
						<TextField
							label="Description"
							fullWidth
							multiline
							rows={2}
							value={formData.description}
							onChange={(e) =>
								setFormData({
									...formData,
									description: e.target.value,
								})
							}
							sx={{ mb: 3 }}
						/>

						<Box
							display="flex"
							justifyContent="space-between"
							mb={2}
						>
							<Typography variant="h6">Data Table</Typography>
							<Box>
								<Button
									size="small"
									onClick={addColumn}
									startIcon={<Add />}
									sx={{ mr: 1 }}
								>
									Add Column
								</Button>
								<Button
									size="small"
									onClick={addRow}
									startIcon={<Add />}
								>
									Add Row
								</Button>
							</Box>
						</Box>

						<TableContainer
							component={Paper}
							sx={{ maxHeight: 400 }}
						>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										{formData.columns.map(
											(column, index) => (
												<TableCell key={index}>
													<Box
														display="flex"
														alignItems="center"
														gap={1}
													>
														<TextField
															value={column}
															onChange={(e) =>
																updateColumnName(
																	column,
																	e.target
																		.value,
																)
															}
															size="small"
															variant="standard"
														/>
														<IconButton
															size="small"
															onClick={() =>
																removeColumn(
																	column,
																)
															}
															disabled={
																formData.columns
																	.length <= 1
															}
														>
															<Delete fontSize="small" />
														</IconButton>
													</Box>
												</TableCell>
											),
										)}
										<TableCell width={50}></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{formData.rows.map((row, rowIndex) => (
										<TableRow key={rowIndex}>
											{formData.columns.map(
												(column, colIndex) => (
													<TableCell key={colIndex}>
														<TextField
															value={
																row[column] ||
																""
															}
															onChange={(e) =>
																updateCellValue(
																	rowIndex,
																	column,
																	e.target
																		.value,
																)
															}
															size="small"
															fullWidth
															variant="standard"
														/>
													</TableCell>
												),
											)}
											<TableCell>
												<IconButton
													size="small"
													onClick={() =>
														removeRow(rowIndex)
													}
													disabled={
														formData.rows.length <=
														1
													}
												>
													<Delete fontSize="small" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button onClick={handleSubmit} variant="contained">
						{editMode ? "Update" : "Create"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Import CSV Dialog */}
			<Dialog
				open={importDialogOpen}
				onClose={() => setImportDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Import CSV File</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2 }}>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 2 }}
						>
							Upload a CSV file to create a data set. The first
							row will be used as column names.
						</Typography>
						<Button variant="outlined" component="label" fullWidth>
							Choose CSV File
							<input
								type="file"
								accept=".csv"
								hidden
								onChange={(e) => setCsvFile(e.target.files[0])}
							/>
						</Button>
						{csvFile && (
							<Alert severity="success" sx={{ mt: 2 }}>
								Selected: {csvFile.name}
							</Alert>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setImportDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleImportCSV}
						variant="contained"
						disabled={!csvFile}
					>
						Import
					</Button>
				</DialogActions>
			</Dialog>

			{/* View Data Dialog */}
			<Dialog
				open={viewDialogOpen}
				onClose={() => setViewDialogOpen(false)}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle>
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems="center"
					>
						<Typography variant="h6">
							{selectedDataSet?.name}
						</Typography>
						<Button
							startIcon={<Download />}
							onClick={() =>
								handleExportCSV(
									selectedDataSet.id,
									selectedDataSet.name,
								)
							}
						>
							Export CSV
						</Button>
					</Box>
				</DialogTitle>
				<DialogContent>
					{selectedDataSet && (
						<>
							{selectedDataSet.description && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 2 }}
								>
									{selectedDataSet.description}
								</Typography>
							)}
							<TableContainer
								component={Paper}
								sx={{ maxHeight: 500 }}
							>
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell width={50}>#</TableCell>
											{selectedDataSet.columns?.map(
												(column, index) => (
													<TableCell key={index}>
														<strong>
															{column}
														</strong>
													</TableCell>
												),
											)}
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedDataSet.rows?.map(
											(row, rowIndex) => (
												<TableRow key={rowIndex}>
													<TableCell>
														{rowIndex + 1}
													</TableCell>
													{selectedDataSet.columns?.map(
														(column, colIndex) => (
															<TableCell
																key={colIndex}
															>
																{row[column] ||
																	""}
															</TableCell>
														),
													)}
												</TableRow>
											),
										)}
									</TableBody>
								</Table>
							</TableContainer>
							<Box display="flex" gap={2} mt={2}>
								<Chip
									label={`${selectedDataSet.columns?.length || 0} columns`}
								/>
								<Chip
									label={`${selectedDataSet.rows?.length || 0} rows`}
								/>
								<Chip
									label={`Used by ${selectedDataSet.usageCount || 0} tests`}
								/>
							</Box>

							{/* Linked Tests Section */}
							{selectedDataSet.linkedTestIds?.length > 0 && (
								<Box sx={{ mt: 3 }}>
									<Typography
										variant="subtitle1"
										gutterBottom
									>
										Linked Tests
									</Typography>
									<Box display="flex" gap={1} flexWrap="wrap">
										{selectedDataSet.linkedTestIds.map(
											(testId) => (
												<Chip
													key={testId}
													label={testId}
													onDelete={() =>
														handleUnlinkTest(
															selectedDataSet.id,
															testId,
														)
													}
													color="primary"
													variant="outlined"
												/>
											),
										)}
									</Box>
								</Box>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setViewDialogOpen(false)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			{/* Link Test Dialog */}
			<Dialog
				open={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Link Test to {linkDataSet?.name}</DialogTitle>
				<DialogContent>
					{availableTests.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ pt: 2 }}
						>
							No available tests to link.
						</Typography>
					) : (
						<Box sx={{ pt: 1 }}>
							{availableTests.filter(canModify).map((test) => (
								<Box
									key={test.id}
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									sx={{
										p: 1.5,
										mb: 1,
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Box>
										<Typography variant="body1">
											{test.name}
										</Typography>
										{test.description && (
											<Typography
												variant="caption"
												color="text.secondary"
											>
												{test.description}
											</Typography>
										)}
									</Box>
									<Button
										size="small"
										variant="outlined"
										startIcon={<LinkIcon />}
										onClick={() => handleLinkTest(test.id)}
									>
										Link
									</Button>
								</Box>
							))}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLinkDialogOpen(false)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default DataSets;
